import { addMinutes, isBefore, set } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { first, pick, toPairs } from "lodash";
import { v4 as uuidv4 } from "uuid";
import {
  ACCEPTED_STATUS,
  CLAIMED_STATUS,
  DEFAULT_LOCATION_DATA,
  DELIVERED_STATUS,
  LARGE_PACKAGE_TYPE,
  MEDIUM_PACKAGE_TYPE,
  PENDING_STATUS,
  SMALL_PACKAGE_TYPE,
  WORKING_HOURS_CONFIG,
} from "../../constants";
import { format, noonOfDay } from "../date-fns";
import i18n from "../i18next";

export const generateNewPackage = () => ({
  reference: uuidv4(),
  parcelCustomerId: "",
  type: SMALL_PACKAGE_TYPE,
  quantity: 1,
});

export const generateNewDrop = () => ({
  reference: uuidv4(),
  ...DEFAULT_LOCATION_DATA,
  packages: [generateNewPackage()],
});

export const prettifyDropReference = (fullRef) => first(fullRef.split("-")).toUpperCase();

export const prettifyParcelCustomerIds = (packages) => {
  return packages.map(({ parcelCustomerId }) => parcelCustomerId).join(", ");
};

export const prettifyRiderName = (name) => name || "N/A";

export const prettifyLocation = ({ location, postcode }) => `${location || ""} | ${postcode || ""}`;

export const calculatePackagesAmount = (drops = []) => {
  return drops.reduce((prevTotal, { packages }) => {
    return prevTotal + packages.reduce((prevAmount, { quantity = 1 }) => prevAmount + quantity, 0);
  }, 0);
};

export const calculatePackagesAmountByType = (packages = []) => {
  return packages.reduce(
    (prev, { type, quantity }) => {
      if (type === SMALL_PACKAGE_TYPE) return { ...prev, [SMALL_PACKAGE_TYPE]: prev[SMALL_PACKAGE_TYPE] + quantity };
      if (type === MEDIUM_PACKAGE_TYPE) return { ...prev, [MEDIUM_PACKAGE_TYPE]: prev[MEDIUM_PACKAGE_TYPE] + quantity };
      if (type === LARGE_PACKAGE_TYPE) return { ...prev, [LARGE_PACKAGE_TYPE]: prev[LARGE_PACKAGE_TYPE] + quantity };
      return { ...prev, [type]: quantity };
    },
    { [SMALL_PACKAGE_TYPE]: 0, [MEDIUM_PACKAGE_TYPE]: 0, [LARGE_PACKAGE_TYPE]: 0 }
  );
};

export const prettifyPackagesAmount = (packages) => {
  return toPairs(calculatePackagesAmountByType(packages))
    .reduce((prev, [type, quantity]) => {
      return quantity === 0
        ? prev
        : [...prev, `${quantity} ${i18n.t([`utils.packageSizes.${type}`, type]).toLocaleLowerCase()}`];
    }, [])
    .join(", ");
};

export const checkActiveOrderStatus = (status) => {
  return ACCEPTED_STATUS === status || DELIVERED_STATUS === status || CLAIMED_STATUS === status;
};

export const checkCancellationAvailability = (status) => [PENDING_STATUS, ACCEPTED_STATUS].includes(status);

export function generateTimePickerData(date, config = {}) {
  const { startTime: st, endTime: et, withoutPastLimit = false, timezone } = config;

  const startHour = st || WORKING_HOURS_CONFIG.start;
  const endHour = et || WORKING_HOURS_CONFIG.end;
  const interval = 15;

  const userDate = utcToZonedTime(noonOfDay(date), timezone);
  const now = utcToZonedTime(new Date(), timezone);

  let currentTime = set(userDate, { hours: startHour, minutes: 0, seconds: 0, milliseconds: 0 });
  const endTime = set(userDate, { hours: endHour, minutes: 0, seconds: 0, milliseconds: 0 });

  const options = [];

  while (!isBefore(endTime, currentTime)) {
    const optionISO = zonedTimeToUtc(currentTime, timezone).toISOString();
    const optionLabel = format(currentTime, "HH:mm", timezone);

    if (withoutPastLimit || isBefore(now, currentTime)) {
      options.push({ value: optionISO, label: optionLabel });
    }

    currentTime = addMinutes(currentTime, interval);
  }

  return options;
}

export const getPriceDiscount = ({ starting_price, price }) => {
  const discount = (Number(starting_price) - Number(price)).toFixed(2);
  const percentage = discount ? ((discount * 100) / Number(starting_price)).toFixed(0) : 0;

  return { discount, percentage };
};

export const getPriceWithVat = (priceObj = {}, shouldInclideVat = false) => {
  const { price = 0, total = 0, starting_price = 0, price_vat = 0, total_vat = 0, starting_price_vat = 0 } = priceObj;

  const { discount, percentage } = getPriceDiscount({ starting_price, price: price || total });

  const totalPrice = shouldInclideVat ? price_vat || total_vat : price || total;
  const startingPrice = shouldInclideVat ? starting_price_vat : starting_price;

  const vat = (shouldInclideVat ? Number(price_vat || total_vat) - Number(price || total) : 0).toFixed(2);

  return {
    discount: { discount, percentage },
    vat,
    totalPrice,
    startingPrice,
    ...pick(priceObj, ["currency", "distance"]),
  };
};

export const uniqueReferenceNumber = () =>
  (Date.now().toString(36) + Math.random().toString(36).substr(2, 5)).toUpperCase();
